/**
 * Utilitário para enriquecer metadados de filmes/séries através do TMDB
 * (The Movie Database) em pt-BR quando a lista IPTV vier sem sinopse/capa completa.
 */

const TMDB_API_KEY = '1cf50e6248dc270629e802686245c2c8'; // Chave ativa e válida v3
const TMDB_BASE_URL = 'https://api.themoviedb.org/3';

export interface TmdbMovieMetadata {
  synopsis?: string;
  poster?: string;
  backdrop?: string;
  rating?: number;
  releaseDate?: string;
  genres?: string[];
}

// Limpa sufixos, prefixos e tags comuns de IPTV para maximizar a precisão da busca no TMDB
export function cleanTitleForSearch(rawTitle: string): { title: string; year?: number } {
  if (!rawTitle) return { title: '' };

  let title = rawTitle;

  // 1. Extrai ano se houver formato (2025) ou [2026]
  let year: number | undefined = undefined;
  const yearMatch = title.match(/[\(\[\{]?(19\d\d|20\d\d)[\)\]\}]?/);
  if (yearMatch) {
    const parsedYear = parseInt(yearMatch[1], 10);
    if (parsedYear >= 1900 && parsedYear <= 2030) {
      year = parsedYear;
    }
  }

  // 2. Remove prefixos clássicos de listas IPTV
  title = title.replace(/^(?:\[.*?\]|\(.*?\))\s*/g, '');
  title = title.replace(/^(?:FILMES|FILME|SÉRIES|SERIES|VOD SÉRIES|VOD|TOP\s*\d+|LANÇAMENTOS|LANCAMENTOS|NOVELAS|ANIMES|DORAMAS|DESENHOS|DUB|LEG)\s*[\-:|•/]\s*/i, '');

  // 3. Remove conteúdo entre colchetes, parênteses e chaves
  title = title
    .replace(/\[.*?\]/g, '')
    .replace(/\(.*?\)/g, '')
    .replace(/\{.*?\}/g, '');

  // 4. Remove tags de resolução, áudio e formato soltas
  title = title.replace(/\b(?:fhd|4k|uhd|hd|sd|dual|dublado|dub|legendado|leg|nacional|bluray|web\-dl|webrip|brrip|dvdrip|hevc|1080p|720p|2160p|h\.?264|h\.?265|x264|x265|multi|5\.1|7\.1|imax|hdr|hdr10|dolby|atmos)\b/gi, '');

  // 5. Normaliza hífens e separadores que quebram a busca fulltext do TMDB
  title = title
    .replace(/[\-\–\|•/_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return { title, year };
}

// Cache em memória durante a sessão para economizar requisições
const metadataCache = new Map<string, TmdbMovieMetadata>();

/**
 * Busca sinopse e detalhes do filme no TMDB com cascata inteligente
 */
export async function fetchTmdbMovieInfo(rawTitle: string, explicitYear?: number): Promise<TmdbMovieMetadata | null> {
  const { title, year: parsedYear } = cleanTitleForSearch(rawTitle);
  const searchYear = explicitYear || parsedYear;

  if (!title || title.length < 2) return null;

  const cacheKey = `movie_${title.toLowerCase()}_${searchYear || 'all'}`;
  if (metadataCache.has(cacheKey)) {
    return metadataCache.get(cacheKey)!;
  }

  try {
    let url = `${TMDB_BASE_URL}/search/movie?api_key=${TMDB_API_KEY}&language=pt-BR&query=${encodeURIComponent(title)}&include_adult=false`;
    if (searchYear) {
      url += `&year=${searchYear}`;
    }

    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    let results: any[] = data.results || [];

    // Se com ano não encontrou ou não veio com sinopse, tenta busca ampla sem ano
    const hasValidOverview = results.some((r) => r.overview && r.overview.trim().length > 20);
    if (results.length === 0 || !hasValidOverview) {
      if (searchYear) {
        const fallbackUrl = `${TMDB_BASE_URL}/search/movie?api_key=${TMDB_API_KEY}&language=pt-BR&query=${encodeURIComponent(title)}&include_adult=false`;
        const fallbackRes = await fetch(fallbackUrl);
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          if (fallbackData.results && fallbackData.results.length > 0) {
            results = fallbackData.results;
          }
        }
      }
    }

    if (results.length === 0) return null;

    // Prioriza o item que tiver a sinopse em português preenchida
    const bestItem = results.find((r) => r.overview && r.overview.trim().length > 20) || results[0];
    return processTmdbMovieResult(bestItem, cacheKey);
  } catch (error) {
    console.warn('[TMDB] Falha ao buscar detalhes do filme:', error);
    return null;
  }
}

/**
 * Busca sinopse e detalhes de séries no TMDB com cascata inteligente
 */
export async function fetchTmdbSeriesInfo(rawTitle: string, explicitYear?: number): Promise<TmdbMovieMetadata | null> {
  const { title, year: parsedYear } = cleanTitleForSearch(rawTitle);
  const searchYear = explicitYear || parsedYear;

  if (!title || title.length < 2) return null;

  const cacheKey = `tv_${title.toLowerCase()}_${searchYear || 'all'}`;
  if (metadataCache.has(cacheKey)) {
    return metadataCache.get(cacheKey)!;
  }

  try {
    let url = `${TMDB_BASE_URL}/search/tv?api_key=${TMDB_API_KEY}&language=pt-BR&query=${encodeURIComponent(title)}&include_adult=false`;
    if (searchYear) {
      url += `&first_air_date_year=${searchYear}`;
    }

    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    let results: any[] = data.results || [];

    // Em séries, busca sem ano frequentemente encontra mais rápido
    const hasValidOverview = results.some((r) => r.overview && r.overview.trim().length > 20);
    if (results.length === 0 || !hasValidOverview) {
      if (searchYear) {
        const fallbackUrl = `${TMDB_BASE_URL}/search/tv?api_key=${TMDB_API_KEY}&language=pt-BR&query=${encodeURIComponent(title)}&include_adult=false`;
        const fallbackRes = await fetch(fallbackUrl);
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          if (fallbackData.results && fallbackData.results.length > 0) {
            results = fallbackData.results;
          }
        }
      }
    }

    if (results.length === 0) return null;

    // Prioriza o resultado com sinopse em português
    const bestItem = results.find((r) => r.overview && r.overview.trim().length > 20) || results[0];
    return processTmdbTvResult(bestItem, cacheKey);
  } catch (error) {
    console.warn('[TMDB] Falha ao buscar detalhes da série:', error);
    return null;
  }
}

function processTmdbMovieResult(movie: any, cacheKey: string): TmdbMovieMetadata {
  const meta: TmdbMovieMetadata = {
    synopsis: movie.overview || undefined,
    poster: movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : undefined,
    backdrop: movie.backdrop_path ? `https://image.tmdb.org/t/p/original${movie.backdrop_path}` : undefined,
    rating: movie.vote_average ? Math.round(movie.vote_average * 10) / 10 : undefined,
    releaseDate: movie.release_date || undefined
  };

  metadataCache.set(cacheKey, meta);
  return meta;
}

function processTmdbTvResult(tv: any, cacheKey: string): TmdbMovieMetadata {
  const meta: TmdbMovieMetadata = {
    synopsis: tv.overview || undefined,
    poster: tv.poster_path ? `https://image.tmdb.org/t/p/w500${tv.poster_path}` : undefined,
    backdrop: tv.backdrop_path ? `https://image.tmdb.org/t/p/original${tv.backdrop_path}` : undefined,
    rating: tv.vote_average ? Math.round(tv.vote_average * 10) / 10 : undefined,
    releaseDate: tv.first_air_date || undefined
  };

  metadataCache.set(cacheKey, meta);
  return meta;
}

